import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";

import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TYPOGRAPHY } from "@tKgpco/theme/design";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    showDocuments = "showDocuments",
    showAttributes = "showAttributes",
    attributesPosition = "attributesPosition",
    documentsPosition = "documentsPosition",
}

type Props = WidgetProps & HasProduct;

export interface ProductDetailsSpecificationsStyles {
    specificationsAccordion?: AccordionProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    specsWrapper?: InjectableCss;
    specsHeader?: InjectableCss;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    specificationsAccordion: {
        headingLevel: 2,
    },
    documentGridItem: {
        width: [12, 6, 6, 4, 4],
        css: css`
            padding: 15px 15px 0 !important;
        `,
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
            color: #353432;
            font-size: ${TYPOGRAPHY.size.small};
            font-weight: ${TYPOGRAPHY.weight.bold};
            margin-left: 10px;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
    specsHeader: {
        css: css`
            border-top: 1px solid #3c75b5;
            border-bottom: 1px solid #3c75b5;
            padding: 15px 0;
            margin-bottom: 35px;
            span,
            a {
                padding-right: 80px;
                font-size: ${TYPOGRAPHY.size.base};
                font-weight: ${TYPOGRAPHY.weight.base};
                line-height: 20px;
                display: inline-block;
                cursor: pointer;
            }
            span:first-child,
            span:focus,
            span:active {
                font-weight: ${TYPOGRAPHY.weight.bold};
            }
        `,
    },
    specsWrapper: {
        css: css`
            position: relative;
            section[data-test-selector="productDetails_specifications_specification"],
            section[data-test-selector="productDetails_specifications_documents"],
            section[data-test-selector="productDetails_specifications_attributes"] {
                margin-bottom: 40px;
                > span {
                    font-size: ${TYPOGRAPHY.size.large};
                    color: #3465b1;
                    line-height: 20px;
                    font-weight: ${TYPOGRAPHY.weight.bold};
                    margin-bottom: 20px;
                    display: block;
                }
            }
            .features_applications {
                -moz-column-count: 2;
                -webkit-column-count: 2;
                column-count: 2;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            -moz-column-count: unset;
                            -webkit-column-count: unset;
                            column-count: unset;
                            > div {
                                margin-bottom: 30px;
                            }
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
                h3 {
                    font-size: ${TYPOGRAPHY.size.small};
                    font-weight: ${TYPOGRAPHY.weight.bold};
                }
                span {
                    position: relative;
                    display: block;
                    vertical-align: top;
                    font-size: ${TYPOGRAPHY.size.small};
                    padding-left: 10px;
                    display: list-item;
                    list-style-type: disc;
                    list-style-position: inside;
                    // &:after {
                    //     content: "";
                    //     height: 3px;
                    //     width: 3px;
                    //     border-radius: 50%;
                    //     display: inline-block;
                    //     position: absolute;
                    //     top: 45%;
                    //     left: 0;
                    //     background: #000;
                    // }
                }
            }
            .tech_specs {
                border: 1px solid #d5d5d5;
                display: table;
                width: 100%;
                div {
                    width: auto;
                    border-bottom: 1px solid #d5d5d5;
                    clear: both;
                    span:first-child {
                        display: inline-block;
                        width: 20%;
                        font-size: ${TYPOGRAPHY.size.small};
                        line-height: 24px;
                        padding: 5px 0 5px 18px;
                        font-weight: ${TYPOGRAPHY.weight.bold};
                        vertical-align: top;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    display: block;
                                    width: 100%;
                                    padding: 5px 0 5px 18px;
                                `,
                                css`
                                    width: 50%;
                                `,
                                null,
                                null,
                                null,
                            ])}
                    }
                    span:last-child {
                        width: 80%;
                        border-left: 1px solid #d5d5d5;
                        display: inline-block;
                        font-size: ${TYPOGRAPHY.size.smallest};
                        line-height: 24px;
                        padding: 6px 0 6px 18px;
                        color: #353432;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    display: block;
                                    width: 100%;
                                    padding: 0 0 6px 18px;
                                    border: 0;
                                `,
                                css`
                                    width: 50%;
                                `,
                                null,
                                null,
                                null,
                            ])}
                    }
                    &:nth-child(even) {
                        background-color: #f7fafc;
                    }
                }
            }
        `,
    },
};

const styles = specificationsStyles;
const StyledSection = getStyledWrapper("section");

const ProductDetailsSpecifications: React.FC<Props> = ({
    product: { brand, attributeTypes, documents, specifications, properties },
    fields: { showDocuments, showAttributes, documentsPosition, attributesPosition },
}) => {
    const renderingDocuments = showDocuments && documents && documents.length > 0;
    const renderingAttributes = showAttributes && (brand || (attributeTypes && attributeTypes?.length > 0));
    const renderingSpecifications = specifications && specifications.length > 0;
    const documentsFirst = documentsPosition === "first";
    const attributesFirst = attributesPosition === "first";
    const customSpecification =
        properties?.isReel?.toLowerCase() === "true"
            ? specifications?.map(detail => {
                  if (detail.name === "Description") {
                      return {
                          ...detail,
                          htmlContent: `${
                              detail.htmlContent
                          } <br/> <span style='color:#0b70b7;font-size:16px;font-weight:normal;display:block;margin-top:10px'>${siteMessage(
                              "reel_disclaimer_msg",
                          )} </span>`,
                      };
                  }

                  return detail;
              })
            : specifications;

    if (!renderingDocuments && !renderingAttributes && !renderingSpecifications) {
        return null;
    }
    const resourceClickHandler = () => {
        const resourceHeader = document.getElementById("resourceSelector");
        if (resourceHeader) {
            const { top } = resourceHeader.getBoundingClientRect();
            window.scrollTo({
                top: top + window.pageYOffset - 80,
                behavior: "smooth",
            });
        }
    };
    const specsClickHandler = (id: string) => {
        const specsHeader = document.getElementById(id);
        if (specsHeader) {
            const { top } = specsHeader.getBoundingClientRect();
            window.scrollTo({
                top: top + window.pageYOffset - 80,
                behavior: "smooth",
            });
        }
    };

    const documentsAccordion = renderingDocuments && (
        <StyledSection
            title={translate("Documents")}
            data-test-selector="productDetails_specifications_documents"
            id="resourceSelector"
        >
            <Typography {...styles.attributeTypeLabelText}>{translate("Resources")}</Typography>
            <GridContainer {...styles.documentsContainer}>
                {documents!.map(document => (
                    <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                        <Link
                            href={document.filePath}
                            target="_new"
                            {...styles.documentLink}
                            data-test-selector={`productDetails_${document.id}`}
                        >
                            {/* <IconMemo {...styles.documentIcon} /> */}
                            <LazyImage src="/UserFiles/images/icons/file-pdf-icon.svg" />
                            <Typography {...styles.documentNameText}>{document.name}</Typography>
                        </Link>
                    </GridItem>
                ))}
            </GridContainer>
        </StyledSection>
    );

    const attributesAccordion = renderingAttributes && (
        <StyledSection title={translate("Attributes")} data-test-selector="productDetails_specifications_attributes">
            <Typography {...styles.attributeTypeLabelText}>{translate("Attributes")}</Typography>
            {brand && (
                <StyledWrapper {...styles.attributeTypeWrapper}>
                    <Typography {...styles.attributeTypeLabelText}>{translate("Brand")}:&nbsp;</Typography>
                    <Typography {...styles.attributeTypeValuesText}>{brand.name}</Typography>
                </StyledWrapper>
            )}
            {attributeTypes &&
                attributeTypes.map(attributeType => (
                    <StyledWrapper {...styles.attributeTypeWrapper} key={attributeType.id.toString()}>
                        <Typography {...styles.attributeTypeLabelText}>
                            {attributeType.label || attributeType.name}:&nbsp;
                        </Typography>
                        <Typography {...styles.attributeTypeValuesText}>
                            {attributeType
                                .attributeValues!.map(attributeValue => attributeValue.valueDisplay)
                                .join(", ")}
                        </Typography>
                    </StyledWrapper>
                ))}
        </StyledSection>
    );

    return (
        <>
            <Hidden below="md">
                <StyledSection {...styles.specsHeader}>
                    {renderingSpecifications &&
                        customSpecification?.map(specification => (
                            // eslint-disable-next-line react/jsx-key
                            <Typography
                                {...styles.attributeTypeLabelText}
                                onClick={() =>
                                    specsClickHandler(`${specification.nameDisplay.replace(/[^A-Z0-9]/gi, "")}`)
                                }
                            >
                                {specification.nameDisplay}
                            </Typography>
                        ))}
                    {renderingDocuments && (
                        <Typography {...styles.attributeTypeLabelText} onClick={resourceClickHandler}>
                            {translate("Resources")}
                        </Typography>
                    )}
                </StyledSection>
            </Hidden>
            <StyledSection {...styles.specsWrapper}>
                {attributesFirst && attributesAccordion}

                {renderingSpecifications &&
                    customSpecification?.map(specification => (
                        <StyledSection
                            key={specification.id.toString()}
                            title={specification.nameDisplay}
                            data-test-selector="productDetails_specifications_specification"
                            id={specification.nameDisplay.replace(/[^A-Z0-9]/gi, "")}
                        >
                            <Typography {...styles.attributeTypeLabelText}>{specification.nameDisplay}</Typography>
                            {parse(transformContentHtml(specification.htmlContent), parserOptions)}
                        </StyledSection>
                    ))}

                {!attributesFirst && attributesAccordion}
                {!documentsFirst && documentsAccordion}
            </StyledSection>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsSpecifications),
    definition: {
        displayName: "Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.documentsPosition,
                displayName: "Documents Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 2,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showDocuments,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.attributesPosition,
                displayName: "Attributes Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 4,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showAttributes,
            },
        ],
    },
};

export default widgetModule;
